import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { DsMessageModule, DsMessageTone } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { AdminModeService } from '../../auth/admin-mode/admin-mode.service';
import { AuthenticationService } from '../../auth/authentication/authentication.service';
import { Role } from '../../auth/roles';
import { DisplayedUserEndpointErrorType, UserEndpointError } from '../../auth/user/user';
import { UserService } from '../../auth/user/user.service';
import { useI18n } from '../i18n/services/i18n.service';
import { TopSectionComponent } from '../navigation/components/top-section/top-section.component';

@Component({
  selector: 'cdf-unauthorized-page',
  template: `
    <cdf-top-section>
      <ds-message [messageText]="message().text" [tone]="message().tone" />
    </cdf-top-section>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DsMessageModule, TopSectionComponent],
})
export class UnauthorizedPage {
  protected readonly i18n = useI18n();
  private readonly logger = inject(NGXLogger);

  private readonly authService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  private readonly adminModeService = inject(AdminModeService);

  readonly allowedRoles = input<readonly Role[]>([]);

  protected readonly message = computed((): { text: string; tone: DsMessageTone } => {
    const error = this.userService.userError();
    if (this.userService.userSignal() && !this.userService.userIsAllowed(this.allowedRoles())) {
      return { text: this.i18n('forbidden.MISSING_ROLE'), tone: 'critical' };
    } else if (this.userService.userSignal() && !this.userService.userOutletIsWhitelisted()) {
      if (this.adminModeService.adminModeReachable() && this.adminModeService.hasAdminReadPermissions()) {
        return { text: this.i18n('forbidden.NOT_WHITELISTED_ADMIN'), tone: 'caution' };
      } else {
        return { text: this.i18n('forbidden.NOT_WHITELISTED'), tone: 'critical' };
      }
    } else if (error instanceof UserEndpointError) {
      if (error.displayedType) {
        return { text: this.i18n.enum(error.displayedType, 'forbidden'), tone: 'critical' };
      } else {
        return { text: this.i18n('forbidden.ERROR_CODE', { errorCode: error.message }), tone: 'critical' };
      }
    } else {
      this.logger.error('presenting unauthorized page with unknown error', error);
      return { text: this.i18n('forbidden.UNKNOWN'), tone: 'critical' };
    }
  });

  private readonly reLogInOnOAuthNoStrong = effect(() => {
    const isInternalAuth = !this.authService.awpAuthInfoInput();
    const userError = this.userService.userError();
    const isStrongAuthError =
      userError instanceof UserEndpointError &&
      userError.errorType === DisplayedUserEndpointErrorType.STRONG_AUTHENTICATION_REQUIRED;
    if (isInternalAuth && isStrongAuthError) {
      this.authService.logOut();
    }
  });
}
